import React, { useState, useEffect } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DialogContentText,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import ResizablePanels from "../ResizablePanels";
import { ResponsiveProvider } from "../../context/ResponsiveContext";
import { connect } from "react-redux";
import {
  createLibraryCardRequest,
  fetchLibraryCardsRequest,
  getLibraryCardRequest,
  listCardsRequest,
  fetchCardDetailsRequest,
} from "../../actions/libraryCardActions";
import { getPexelsImages } from "../../actions/mediaActions";
import { useTheme } from "@mui/material/styles";

// Available section types
const SECTION_TYPES = [
  { id: "header", label: "Header", icon: "📝" },
  { id: "products", label: "Products", icon: "🛍️" },
  { id: "hero", label: "Hero", icon: "🎯" },
  { id: "content", label: "Content", icon: "📄" },
  { id: "sidebar", label: "Sidebar", icon: "📑" },
  { id: "gallery", label: "Gallery", icon: "🖼️" },
  { id: "cta", label: "Call to Action", icon: "🎯" },
];

// Define the available components that can be dragged into sections
const COMPONENT_TYPES = [
  // Text Components
  {
    id: "heading",
    label: "Heading",
    icon: "H1",
    category: "text",
    defaultSpan: 12,
  },
  {
    id: "subheading",
    label: "Subheading",
    icon: "H2",
    category: "text",
    defaultSpan: 12,
  },
  {
    id: "paragraph",
    label: "Paragraph",
    icon: "¶",
    category: "text",
    defaultSpan: 12,
  },
  { id: "quote", label: "Quote", icon: "❝", category: "text", defaultSpan: 6 },

  // Media Components
  {
    id: "image",
    label: "Image",
    icon: "🖼️",
    category: "media",
    defaultSpan: 6,
  },
  {
    id: "video",
    label: "Video",
    icon: "🎥",
    category: "media",
    defaultSpan: 12,
  },
  { id: "icon", label: "Icon", icon: "⭐", category: "media", defaultSpan: 3 },

  // Interactive Components
  {
    id: "button",
    label: "Button",
    icon: "🔘",
    category: "interactive",
    defaultSpan: 4,
  },
  {
    id: "link",
    label: "Link",
    icon: "🔗",
    category: "interactive",
    defaultSpan: 4,
  },
  {
    id: "form",
    label: "Form",
    icon: "📝",
    category: "interactive",
    defaultSpan: 8,
  },

  // Layout Components
  {
    id: "container",
    label: "Container",
    icon: "📦",
    category: "layout",
    defaultSpan: 12,
  },
  {
    id: "grid",
    label: "Grid",
    icon: "📐",
    category: "layout",
    defaultSpan: 12,
  },
  {
    id: "spacer",
    label: "Spacer",
    icon: "↕️",
    category: "layout",
    defaultSpan: 12,
  },
];

// Define which sections support columns
const SECTIONS_WITH_COLUMNS = ["products", "gallery"];

// Draggable section component
const DraggableSection = ({ type, onDragStart }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "SECTION",
    item: { type: type.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <Paper
      ref={drag}
      sx={{
        p: 2,
        mb: 2,
        cursor: "move",
        opacity: isDragging ? 0.5 : 1,
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <DragIcon />
      <Typography>
        {type.icon} {type.label}
      </Typography>
    </Paper>
  );
};

// Main drop zone container
const DropZoneContainer = ({ children, onDrop }) => {
  const [{ isOver }, drop] = useDrop({
    accept: "SECTION",
    drop: (item, monitor) => {
      console.log("Drop monitor:", monitor.didDrop());
      if (!monitor.didDrop()) {
        onDrop(item);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <Box
      ref={drop}
      sx={{
        minHeight: "200px",
        border: "2px dashed",
        borderColor: isOver ? "primary.main" : "divider",
        borderRadius: 1,
        p: 2,
        backgroundColor: isOver ? "action.hover" : "background.paper",
      }}
    >
      {children.length === 0 ? (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
          }}
        >
          <Typography>Drag sections here to build your template</Typography>
        </Box>
      ) : (
        children
      )}
    </Box>
  );
};

// Droppable section component
const DroppableSection = ({ section, index, onDrop, onDelete, onEdit }) => {
  const [{ isOver }, drop] = useDrop({
    accept: "SECTION",
    drop: (item) => {
      onDrop(item, index);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const renderContent = () => {
    if (!section.content) return null;

    // Render product section
    if (
      section.type === "products" &&
      section.content.items &&
      Array.isArray(section.content.items)
    ) {
      const products = section.content.items;
      const columns = section.content.columns || 3;

      return (
        <Box sx={{ mt: 2 }}>
          {section.content.title && (
            <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
              {section.content.title}
            </Typography>
          )}
          {section.content.description && (
            <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
              {section.content.description}
            </Typography>
          )}
          <Grid container spacing={2}>
            {products.map((product, idx) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={12 / columns}
                key={product.id || idx}
              >
                <Paper
                  sx={{
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "300px",
                      mb: 2,
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No image
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {product.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, flex: 1 }}>
                    {product.description}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                    {product.price}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {product.isNew && (
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1,
                          py: 0.5,
                          bgcolor: "primary.main",
                          color: "white",
                          borderRadius: 1,
                        }}
                      >
                        New
                      </Typography>
                    )}
                    {product.isBestSeller && (
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1,
                          py: 0.5,
                          bgcolor: "secondary.main",
                          color: "white",
                          borderRadius: 1,
                        }}
                      >
                        Best Seller
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    // Render hero section
    if (section.type === "hero" && section.content.imageUrl) {
      return (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: "16/9",
              mb: 2,
              overflow: "hidden",
            }}
          >
            <img
              src={section.content.imageUrl}
              alt={section.content.title || "Section image"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: 4,
                color: "white",
                textAlign: "center",
              }}
            >
              {section.content.title && (
                <Typography
                  variant="h2"
                  sx={{
                    mb: 2,
                    fontWeight: "bold",
                    textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                  }}
                >
                  {section.content.title}
                </Typography>
              )}
              {section.content.description && (
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    maxWidth: "800px",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
                  }}
                >
                  {section.content.description}
                </Typography>
              )}
              {section.content.button && (
                <Button
                  variant="contained"
                  color="primary"
                  href={section.content.button.link}
                  size="large"
                  sx={{
                    fontSize: "1.2rem",
                    padding: "12px 24px",
                  }}
                >
                  {section.content.button.text}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      );
    }

    // Generic content rendering
    return (
      <Box sx={{ mt: 2 }}>
        {section.content.title && (
          <Typography variant="h5" sx={{ mb: 2 }}>
            {section.content.title}
          </Typography>
        )}
        {section.content.description && (
          <Typography variant="body1" sx={{ mb: 2 }}>
            {section.content.description}
          </Typography>
        )}
        {section.content.imageUrl && (
          <Box
            sx={{
              width: "100%",
              height: "200px",
              mb: 2,
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <img
              src={section.content.imageUrl}
              alt={section.content.title || "Section image"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Paper
      ref={drop}
      sx={{
        p: 2,
        mb: 2,
        minHeight: "100px",
        border: "2px dashed",
        borderColor: isOver ? "primary.main" : "divider",
        position: "relative",
        backgroundColor: isOver ? "action.hover" : "background.paper",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <DragIcon />
        <Typography variant="h6">
          {section.icon} {section.label}
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <IconButton onClick={() => onEdit(index)} size="small">
            <AddIcon />
          </IconButton>
          <IconButton onClick={() => onDelete(index)} size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
      {renderContent()}
    </Paper>
  );
};

// Section configuration dialog
const SectionConfigDialog = ({
  open,
  onClose,
  section,
  onSave,
  sections,
  layout,
  spacing,
  editingSectionIndex,
  setSections,
}) => {
  const [config, setConfig] = useState(
    section || { type: "", content: {}, fields: [] }
  );
  const [components, setComponents] = useState(section?.components || []);
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dialogSize, setDialogSize] = useState({ width: 600, height: 400 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [content, setContent] = useState({
    title: section?.content?.title || "",
    description: section?.content?.description || "",
    imageUrl: section?.content?.imageUrl || "",
    button: section?.content?.button || { text: "", link: "" },
  });

  // Update content when section changes
  useEffect(() => {
    if (section) {
      setContent({
        title: section.content?.title || "",
        description: section.content?.description || "",
        imageUrl: section.content?.imageUrl || "",
        button: section.content?.button || { text: "", link: "" },
      });
      setComponents(section.components || []);
    }
  }, [section]);

  const handleMouseDown = (e) => {
    if (e.target.closest(".dialog-drag-handle")) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dialogPosition.x,
        y: e.clientY - dialogPosition.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDialogPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    } else if (isResizing) {
      const newWidth = Math.max(
        400,
        resizeStart.width + (e.clientX - resizeStart.x)
      );
      const newHeight = Math.max(
        300,
        resizeStart.height + (e.clientY - resizeStart.y)
      );
      setDialogSize({ width: newWidth, height: newHeight });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const handleResizeStart = (e) => {
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: dialogSize.width,
      height: dialogSize.height,
    });
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing]);

  const handleContentChange = (field, value) => {
    if (field === "buttonText" || field === "buttonLink") {
      const newContent = {
        ...content,
        button: {
          ...content.button,
          [field === "buttonText" ? "text" : "link"]: value,
        },
      };
      setContent(newContent);
      // Update the section for live preview only
      onSave({
        ...sections[editingSectionIndex],
        content: newContent,
        components: components,
      });
    } else {
      const newContent = {
        ...content,
        [field]: value,
      };
      setContent(newContent);
      // Update the section for live preview only
      onSave({
        ...sections[editingSectionIndex],
        content: newContent,
        components: components,
      });
    }
  };

  const handleSaveAndClose = () => {
    // First update the sections state with the latest content
    const updatedSections = [...sections];
    updatedSections[editingSectionIndex] = {
      ...updatedSections[editingSectionIndex],
      content: content,
      components: components,
    };
    setSections(updatedSections);

    // Close the dialog
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      hideBackdrop={true}
      sx={{
        "& .MuiDialog-container": {
          backgroundColor: "transparent",
        },
      }}
      PaperProps={{
        sx: {
          position: "fixed",
          m: 0,
          top: dialogPosition.y,
          left: dialogPosition.x,
          width: dialogSize.width,
          height: dialogSize.height,
          maxHeight: "90vh",
          cursor: isDragging ? "grabbing" : "default",
          backgroundColor: "rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(3px)",
          display: "flex",
          flexDirection: "column",
          "& .MuiDialogContent-root": {
            overflow: "auto",
            flex: 1,
          },
        },
      }}
    >
      <DialogTitle
        onMouseDown={handleMouseDown}
        sx={{
          cursor: "grab",
          userSelect: "none",
          "&:active": {
            cursor: "grabbing",
          },
          flexShrink: 0,
        }}
        className="dialog-drag-handle"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DragIcon />
          <Typography>
            Configure {SECTION_TYPES.find((t) => t.id === config.type)?.label}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Paper
            sx={{
              p: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "rgba(255, 255, 255, 0.3)",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Content
            </Typography>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Title"
                value={content.title}
                onChange={(e) => handleContentChange("title", e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={content.description}
                onChange={(e) =>
                  handleContentChange("description", e.target.value)
                }
                sx={{ flex: 2 }}
              />
              <TextField
                fullWidth
                label="Image URL"
                value={content.imageUrl}
                onChange={(e) =>
                  handleContentChange("imageUrl", e.target.value)
                }
                sx={{ flex: 1 }}
              />
              <TextField
                fullWidth
                label="Button Text"
                value={content.button.text}
                onChange={(e) =>
                  handleContentChange("buttonText", e.target.value)
                }
                sx={{ flex: 1 }}
              />
              <TextField
                fullWidth
                label="Button Link"
                value={content.button.link}
                onChange={(e) =>
                  handleContentChange("buttonLink", e.target.value)
                }
                sx={{ flex: 1 }}
              />
            </Stack>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSaveAndClose} variant="contained">
          Save
        </Button>
      </DialogActions>
      <Box
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: "20px",
          height: "20px",
          cursor: "nwse-resize",
          "&::after": {
            content: '""',
            position: "absolute",
            right: "4px",
            bottom: "4px",
            width: "12px",
            height: "12px",
            borderRight: "2px solid rgba(0, 0, 0, 0.3)",
            borderBottom: "2px solid rgba(0, 0, 0, 0.3)",
          },
        }}
        onMouseDown={handleResizeStart}
      />
    </Dialog>
  );
};

// Product section configuration dialog
const ProductSectionEditor = ({
  open,
  onClose,
  section,
  onSave,
  sections,
  layout,
  spacing,
  editingSectionIndex,
  setSections,
  getPexelsImages,
  pexelsImages,
}) => {
  const [config, setConfig] = useState(
    section || { type: "", content: {}, fields: [] }
  );
  const [components, setComponents] = useState(section?.components || []);
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dialogSize, setDialogSize] = useState({ width: 800, height: 600 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [content, setContent] = useState({
    title: section?.content?.title || "",
    description: section?.content?.description || "",
    columns: section?.content?.columns || 3,
    items: section?.content?.items || [],
  });
  const [showPexelsSearch, setShowPexelsSearch] = useState(false);
  const [searchTerms, setSearchTerms] = useState("");
  const [selectedProductIndex, setSelectedProductIndex] = useState(null);

  // Update content when section changes
  useEffect(() => {
    if (section) {
      setContent({
        title: section.content?.title || "",
        description: section.content?.description || "",
        columns: section.content?.columns || 3,
        items: section.content?.items || [],
      });
      setComponents(section.components || []);
    }
  }, [section]);

  const handleMouseDown = (e) => {
    if (e.target.closest(".dialog-drag-handle")) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dialogPosition.x,
        y: e.clientY - dialogPosition.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDialogPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    } else if (isResizing) {
      const newWidth = Math.max(
        400,
        resizeStart.width + (e.clientX - resizeStart.x)
      );
      const newHeight = Math.max(
        300,
        resizeStart.height + (e.clientY - resizeStart.y)
      );
      setDialogSize({ width: newWidth, height: newHeight });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const handleResizeStart = (e) => {
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: dialogSize.width,
      height: dialogSize.height,
    });
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing]);

  const handleContentChange = (field, value) => {
    const newContent = {
      ...content,
      [field]: value,
    };
    setContent(newContent);
    // Update the section for live preview only
    onSave({
      ...sections[editingSectionIndex],
      content: newContent,
      components: components,
    });
  };

  const handleAddProduct = () => {
    const newItems = [
      ...content.items,
      {
        id: `product-${Date.now()}`,
        name: "",
        description: "",
        price: "",
        image: "",
        isNew: false,
        isBestSeller: false,
      },
    ];
    handleContentChange("items", newItems);
  };

  const handleUpdateProduct = (index, field, value) => {
    const newItems = [...content.items];
    if (field === "isNew" || field === "isBestSeller") {
      newItems[index] = {
        ...newItems[index],
        [field]: value,
      };
    } else {
      newItems[index] = {
        ...newItems[index],
        [field]: value,
      };
    }
    handleContentChange("items", newItems);
  };

  const handleDeleteProduct = (index) => {
    const newItems = [...content.items];
    newItems.splice(index, 1);
    handleContentChange("items", newItems);
  };

  const handleSaveAndClose = () => {
    // First update the sections state with the latest content
    const updatedSections = [...sections];
    updatedSections[editingSectionIndex] = {
      ...updatedSections[editingSectionIndex],
      content: content,
      components: components,
    };
    setSections(updatedSections);

    // Close the dialog
    onClose();
  };

  const handleOpenPexelsSearch = (index) => {
    setSelectedProductIndex(index);
    setShowPexelsSearch(true);
  };

  const handleClosePexelsSearch = () => {
    setShowPexelsSearch(false);
    setSelectedProductIndex(null);
  };

  const handleSearchPexels = () => {
    if (searchTerms.trim()) {
      getPexelsImages(searchTerms);
    }
  };

  const handleSelectPexelsImage = (index) => {
    if (selectedProductIndex !== null && pexelsImages && pexelsImages[index]) {
      const selectedImage = pexelsImages[index];
      const imageUrl = selectedImage.src.large;
      handleUpdateProduct(selectedProductIndex, "image", imageUrl);
      handleClosePexelsSearch();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        hideBackdrop={true}
        sx={{
          "& .MuiDialog-container": {
            backgroundColor: "transparent",
          },
        }}
        PaperProps={{
          sx: {
            position: "fixed",
            m: 0,
            top: dialogPosition.y,
            left: dialogPosition.x,
            width: dialogSize.width,
            height: dialogSize.height,
            maxHeight: "90vh",
            cursor: isDragging ? "grabbing" : "default",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            backdropFilter: "blur(3px)",
            display: "flex",
            flexDirection: "column",
            "& .MuiDialogContent-root": {
              overflow: "auto",
              flex: 1,
            },
          },
        }}
      >
        <DialogTitle
          onMouseDown={handleMouseDown}
          sx={{
            cursor: "grab",
            userSelect: "none",
            "&:active": {
              cursor: "grabbing",
            },
            flexShrink: 0,
          }}
          className="dialog-drag-handle"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DragIcon />
            <Typography>
              Configure {SECTION_TYPES.find((t) => t.id === config.type)?.label}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Paper
              sx={{
                p: 2,
                mb: 2,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Section Content
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Title"
                  value={content.title}
                  onChange={(e) => handleContentChange("title", e.target.value)}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={content.description}
                  onChange={(e) =>
                    handleContentChange("description", e.target.value)
                  }
                />
                <FormControl fullWidth>
                  <InputLabel>Columns</InputLabel>
                  <Select
                    value={content.columns}
                    label="Columns"
                    onChange={(e) =>
                      handleContentChange("columns", e.target.value)
                    }
                  >
                    <MenuItem value={1}>1 Column</MenuItem>
                    <MenuItem value={2}>2 Columns</MenuItem>
                    <MenuItem value={3}>3 Columns</MenuItem>
                    <MenuItem value={4}>4 Columns</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Paper>

            <Paper
              sx={{
                p: 2,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "rgba(255, 255, 255, 0.3)",
                overflow: "auto",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="h6">Products</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddProduct}
                >
                  Add Product
                </Button>
              </Box>

              {content.items.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    opacity: 0.5,
                  }}
                >
                  <Typography>
                    No products added yet. Click "Add Product" to get started.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={3}>
                  {content.items.map((product, index) => (
                    <Paper
                      key={product.id}
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6">
                          Product {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteProduct(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Product Name"
                            placeholder="Enter product name"
                            value={product.name}
                            onChange={(e) =>
                              handleUpdateProduct(index, "name", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Price"
                            placeholder="Enter price (e.g., $99.99)"
                            value={product.price}
                            onChange={(e) =>
                              handleUpdateProduct(
                                index,
                                "price",
                                e.target.value
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Description"
                            placeholder="Enter product description"
                            value={product.description}
                            onChange={(e) =>
                              handleUpdateProduct(
                                index,
                                "description",
                                e.target.value
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <TextField
                              fullWidth
                              label="Image URL"
                              placeholder="Enter image URL or search for an image"
                              value={product.image}
                              onChange={(e) =>
                                handleUpdateProduct(
                                  index,
                                  "image",
                                  e.target.value
                                )
                              }
                            />
                            <Button
                              variant="outlined"
                              startIcon={<SearchIcon />}
                              onClick={() => handleOpenPexelsSearch(index)}
                            >
                              Search
                            </Button>
                          </Box>
                          {product.image && (
                            <Box
                              sx={{
                                mt: 1,
                                width: "100%",
                                height: "300px",
                                borderRadius: 1,
                                overflow: "hidden",
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <img
                                src={product.image}
                                alt={product.name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={product.isNew}
                                  onChange={(e) =>
                                    handleUpdateProduct(
                                      index,
                                      "isNew",
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label="New Product"
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={product.isBestSeller}
                                  onChange={(e) =>
                                    handleUpdateProduct(
                                      index,
                                      "isBestSeller",
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label="Best Seller"
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSaveAndClose} variant="contained">
            Save
          </Button>
        </DialogActions>
        <Box
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: "20px",
            height: "20px",
            cursor: "nwse-resize",
            "&::after": {
              content: '""',
              position: "absolute",
              right: "4px",
              bottom: "4px",
              width: "12px",
              height: "12px",
              borderRight: "2px solid rgba(0, 0, 0, 0.3)",
              borderBottom: "2px solid rgba(0, 0, 0, 0.3)",
            },
          }}
          onMouseDown={handleResizeStart}
        />
      </Dialog>

      {/* Pexels Image Search Dialog */}
      <Dialog
        open={showPexelsSearch}
        onClose={handleClosePexelsSearch}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Search Pexels Images</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Search Terms"
              value={searchTerms}
              onChange={(e) => setSearchTerms(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearchPexels();
                }
              }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearchPexels}
              sx={{ mt: 1 }}
            >
              Search
            </Button>
          </Box>

          {pexelsImages && pexelsImages.length > 0 ? (
            <Grid container spacing={2}>
              {pexelsImages.map((image, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    sx={{
                      p: 1,
                      cursor: "pointer",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                    }}
                    onClick={() => handleSelectPexelsImage(index)}
                  >
                    <img
                      src={image.src.medium}
                      alt={`Pexels Image ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "200px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <Typography color="text.secondary">
                {searchTerms
                  ? "No images found. Try different search terms."
                  : "Enter search terms and click Search to find images."}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePexelsSearch}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Add DraggableComponent component
const DraggableComponent = ({ component, onDragStart }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "COMPONENT",
    item: { type: component.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <Paper
      ref={drag}
      sx={{
        p: 2,
        mb: 1,
        cursor: "move",
        opacity: isDragging ? 0.5 : 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography>{component.icon}</Typography>
      <Typography variant="body2">{component.label}</Typography>
    </Paper>
  );
};

// Add DroppableComponent component
const DroppableComponent = ({ component, index, onDrop, onDelete, onEdit }) => {
  const [{ isOver }, drop] = useDrop({
    accept: "COMPONENT",
    drop: (item) => {
      onDrop(item, index);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <Paper
      ref={drop}
      sx={{
        p: 2,
        mb: 2,
        minHeight: "60px",
        border: "2px dashed",
        borderColor: isOver ? "primary.main" : "divider",
        position: "relative",
        backgroundColor: isOver ? "action.hover" : "background.paper",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography>
          {COMPONENT_TYPES.find((t) => t.id === component.type)?.icon}
        </Typography>
        <Typography variant="body1">
          {COMPONENT_TYPES.find((t) => t.id === component.type)?.label}
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <IconButton onClick={onEdit} size="small">
            <AddIcon />
          </IconButton>
          <IconButton onClick={onDelete} size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
      {component.span && (
        <Typography variant="caption" color="text.secondary">
          Spans {component.span} columns
        </Typography>
      )}
    </Paper>
  );
};

// Add entity type options
const ENTITY_TYPES = [
  { value: "HEROCOMPONENT", label: "Hero Component" },
  { value: "PRODUCTCOMPONENT", label: "Product Component" },
  { value: "PAGEPARTCOMPONENT", label: "Page Part Component" },
  { value: "FEATURECOMPONENT", label: "Feature Component" },
  { value: "TESTIMONIALCOMPONENT", label: "Testimonial Component" },
  { value: "CALLTOACTIONCOMPONENT", label: "Call to Action Component" },
];

const GenericViewDesigner = ({
  initialTemplate,
  onTemplateGenerated,
  startup,
  jwt,
  createLibraryCardRequest,
  fetchLibraryCardsRequest,
  getLibraryCardRequest,
  listCards,
  cardDetails,
  listCardsRequest,
  fetchCardDetailsRequest,
  getPexelsImages,
  pexelsImages,
}) => {
  const theme = useTheme();
  const [sections, setSections] = useState([]);
  const [layout, setLayout] = useState("stack");
  const [spacing, setSpacing] = useState(2);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [content, setContent] = useState({
    title: "",
    description: "",
    imageUrl: "",
    button: { text: "", link: "" },
  });
  const [components, setComponents] = useState([]);
  const fileInputRef = React.useRef(null);

  // Add state for component listing
  const [selectedEntityType, setSelectedEntityType] = useState("");
  const [selectedComponent, setSelectedComponent] = useState("");
  const [savedComponents, setSavedComponents] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [componentName, setComponentName] = useState("");
  const [localStartup, setLocalStartup] = useState(
    localStorage.getItem("STARTUP")
  );

  // Initialize startup from localStorage if not available in Redux
  useEffect(() => {
    if (!startup && !localStartup) {
      const storedStartup = localStorage.getItem("STARTUP");
      console.log("Getting startup from localStorage:", storedStartup);
      setLocalStartup(storedStartup);
    }
  }, [startup]);

  useEffect(() => {
    const effectiveStartup =
      startup || localStartup || localStorage.getItem("STARTUP");
    console.log("UseEffect triggered with:", {
      selectedEntityType,
      startup,
      localStartup,
      effectiveStartup,
      listCardsRequest: !!listCardsRequest,
    });

    if (effectiveStartup) {
      const listTemplatesPayload = {
        operationType: "LIST_LIBRARY_CARDS",
        tenantId: effectiveStartup,
        entityType: selectedEntityType || "HEROCOMPONENT", // Default to HEROCOMPONENT if no type selected
      };
      console.log("Requesting templates with payload:", listTemplatesPayload);
      listCardsRequest(listTemplatesPayload);
    } else {
      console.log("Missing required values:", {
        selectedEntityType,
        startup,
        localStartup,
        effectiveStartup,
        fromLocalStorage: localStorage.getItem("STARTUP"),
      });
    }
  }, [selectedEntityType, startup, localStartup, listCardsRequest]);

  useEffect(() => {
    console.log("Full listCards state:", listCards);
    if (listCards?.data?.listLibraryCards?.pageNames) {
      const pageNames = listCards.data.listLibraryCards.pageNames;
      console.log("Found pageNames:", pageNames);

      if (Array.isArray(pageNames)) {
        const components = pageNames.map((name) => ({
          id: name,
          name: name,
        }));
        console.log("Setting savedComponents:", components);
        setSavedComponents(components);
      } else {
        console.log("PageNames is not an array:", pageNames);
        setSavedComponents([]);
      }
    } else if (Array.isArray(listCards)) {
      // Handle the case where listCards is directly an array (old format)
      const components = listCards.map((name) => ({
        id: name,
        name: name,
      }));
      console.log("Setting savedComponents from direct array:", components);
      setSavedComponents(components);
    } else {
      console.log("No valid listCards data:", listCards);
      setSavedComponents([]);
    }
  }, [listCards]);

  const handleComponentSelect = (event) => {
    const componentId = event.target.value;
    setSelectedComponent(componentId);

    if (componentId) {
      const effectiveStartup =
        startup || localStartup || localStorage.getItem("STARTUP");
      const templatePayload = {
        operationType: "GET_LIBRARY_CARD",
        tenantId: effectiveStartup,
        entityType: selectedEntityType || "HEROCOMPONENT",
        entityId: componentId,
      };

      console.log("Fetching component with payload:", templatePayload);
      getLibraryCardRequest(templatePayload);
    }
  };

  useEffect(() => {
    if (cardDetails) {
      try {
        console.log("Received cardDetails:", cardDetails);
        const cardDetailsObj =
          typeof cardDetails === "string"
            ? JSON.parse(cardDetails)
            : cardDetails;

        if (cardDetailsObj && cardDetailsObj.pageData) {
          console.log(
            "Setting sections from pageData:",
            cardDetailsObj.pageData
          );
          // Set the sections
          setSections(cardDetailsObj.pageData.sections || []);
          // Set the layout
          setLayout(cardDetailsObj.pageData.layout || "stack");
          // Set the spacing
          setSpacing(cardDetailsObj.pageData.spacing || 2);

          // Also update the content state for the current section
          if (
            cardDetailsObj.pageData.sections &&
            cardDetailsObj.pageData.sections.length > 0
          ) {
            const firstSection = cardDetailsObj.pageData.sections[0];
            setContent(
              firstSection.content || {
                title: "",
                description: "",
                imageUrl: "",
                button: { text: "", link: "" },
              }
            );
            console.log("Updated content with:", firstSection.content);
          }
        }
      } catch (error) {
        console.error("Error parsing template data:", error);
        console.error("Raw cardDetails:", cardDetails);
      }
    }
  }, [cardDetails]);

  const handleSaveComponent = () => {
    if (!componentName.trim()) {
      alert("Please enter a name for your component");
      return;
    }

    const effectiveStartup = startup || localStartup;
    if (!effectiveStartup) {
      alert("No startup found. Please ensure you are logged in.");
      return;
    }

    const payload = {
      tenantId: effectiveStartup,
      entityType: selectedEntityType || "HEROCOMPONENT",
      operationType: "SAVE_LIBRARY_CARD",
      entityId: componentName.trim(),
      payload: JSON.stringify({
        startup: componentName.trim(),
        templateName: componentName.trim(),
        type: "hero",
        pageData: {
          sections,
          layout,
          spacing,
        },
      }),
    };

    console.log("Saving component with payload:", payload);
    createLibraryCardRequest(payload);
    setSaveDialogOpen(false);
    setComponentName("");
  };

  const handleDrop = (item, index) => {
    console.log("Drop item:", item);
    const sectionType = SECTION_TYPES.find((t) => t.id === item.type);
    if (sectionType) {
      const newSection = {
        id: `section-${Date.now()}`,
        type: sectionType.id,
        label: sectionType.label,
        icon: sectionType.icon,
        content: {
          title: "",
          description: "",
          imageUrl: "",
          button: { text: "", link: "" },
        },
        components: [],
      };

      const newSections = [...sections];
      if (typeof index === "number") {
        newSections.splice(index, 0, newSection);
      } else {
        newSections.push(newSection);
      }
      setSections(newSections);
    }
  };

  const handleDelete = (index) => {
    const newSections = [...sections];
    newSections.splice(index, 1);
    setSections(newSections);
  };

  const handleEdit = (index) => {
    setEditingSectionIndex(index);
    setConfigDialogOpen(true);
  };

  const handleUpdateSection = (updatedSection) => {
    const newSections = [...sections];
    newSections[editingSectionIndex] = updatedSection;
    setSections(newSections);
  };

  // Render the left panel with sections and components
  const renderLeftPanel = () => (
    <Box sx={{ p: 2, height: "100%", overflow: "auto" }}>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Entity Type (Optional)</InputLabel>
        <Select
          value={selectedEntityType}
          onChange={(e) => setSelectedEntityType(e.target.value)}
          label="Entity Type (Optional)"
        >
          {ENTITY_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedEntityType && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Saved Components</InputLabel>
          <Select
            value={selectedComponent}
            onChange={handleComponentSelect}
            label="Saved Components"
          >
            <MenuItem value="">Select a component</MenuItem>
            {savedComponents && savedComponents.length > 0 ? (
              savedComponents.map((component) => (
                <MenuItem key={component.id} value={component.id}>
                  {component.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                No components available
              </MenuItem>
            )}
          </Select>
        </FormControl>
      )}

      <Typography variant="h6" sx={{ mb: 2 }}>
        Available Sections
      </Typography>
      {SECTION_TYPES.map((type) => (
        <DraggableSection key={type.id} type={type} />
      ))}
    </Box>
  );

  // Render the right panel with the drop zone
  const renderRightPanel = () => (
    <Box sx={{ height: "100%", overflow: "auto", p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Template Editor</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (sections.length > 0) {
                setEditingSectionIndex(0);
                setConfigDialogOpen(true);
              }
            }}
            disabled={sections.length === 0}
          >
            Edit Current View
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setSaveDialogOpen(true)}
            disabled={sections.length === 0}
          >
            Save as Component
          </Button>
        </Box>
      </Box>
      <DropZoneContainer onDrop={handleDrop}>
        {sections.map((section, index) => (
          <DroppableSection
            key={section.id}
            section={section}
            index={index}
            onDrop={handleDrop}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        ))}
      </DropZoneContainer>
    </Box>
  );

  const renderSaveDialog = () => (
    <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
      <DialogTitle>Save Component</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter a name for your component. This will be used to identify it in
          the saved components list.
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label="Component Name"
          type="text"
          fullWidth
          variant="outlined"
          value={componentName}
          onChange={(e) => setComponentName(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
        <Button
          onClick={handleSaveComponent}
          variant="contained"
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <ResponsiveProvider>
      <DndProvider backend={HTML5Backend}>
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
          <ResizablePanels
            leftPanel={renderLeftPanel()}
            rightPanel={renderRightPanel()}
            initialSplit={30}
            minLeftWidth={0}
            maxLeftWidth={100}
            direction="vertical"
          />
          {editingSectionIndex !== null &&
          sections[editingSectionIndex]?.type === "products" ? (
            <ProductSectionEditor
              open={configDialogOpen}
              onClose={() => setConfigDialogOpen(false)}
              section={sections[editingSectionIndex]}
              onSave={handleUpdateSection}
              sections={sections}
              layout={layout}
              spacing={spacing}
              editingSectionIndex={editingSectionIndex}
              setSections={setSections}
              getPexelsImages={getPexelsImages}
              pexelsImages={pexelsImages}
            />
          ) : (
            <SectionConfigDialog
              open={configDialogOpen}
              onClose={() => setConfigDialogOpen(false)}
              section={
                editingSectionIndex !== null
                  ? sections[editingSectionIndex]
                  : null
              }
              onSave={handleUpdateSection}
              sections={sections}
              layout={layout}
              spacing={spacing}
              editingSectionIndex={editingSectionIndex}
              setSections={setSections}
            />
          )}
          {renderSaveDialog()}
        </Box>
      </DndProvider>
    </ResponsiveProvider>
  );
};

const mapStateToProps = (state) => {
  console.log("Redux state in mapStateToProps:", state);
  return {
    startup: state.auth?.startup,
    jwt: state.auth?.jwt,
    listCards: state.libraryCards?.listCards,
    cardDetails: state.libraryCards?.cardDetails,
    pexelsImages: state.media?.pexelsImages,
  };
};

const mapDispatchToProps = {
  createLibraryCardRequest,
  fetchLibraryCardsRequest,
  getLibraryCardRequest,
  listCardsRequest,
  fetchCardDetailsRequest,
  getPexelsImages,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GenericViewDesigner);
